import {useCallback} from "react";

import type {TLevelObject} from "@repo/shared";

import {useGameState} from "~/contexts/gamestate/useGameState";
import {useSelection} from "~/three/selection/useSelection";

export const useEditActions = () => {
  const {getSelectedInstances} = useGameState();
  const {addAndSelect, deleteSelected} = useSelection();

  const handleCopy = useCallback(() => {
    const selectedInstances = getSelectedInstances();
    if (selectedInstances.length === 0) return;

    try {
      const serializedData = JSON.stringify(selectedInstances);
      navigator.clipboard.writeText(serializedData).catch((err) => {
        console.error("Failed to write to clipboard:", err);
      });
    } catch (error) {
      console.error("Error serializing objects:", error);
    }
  }, [getSelectedInstances]);

  const handlePaste = useCallback(() => {
    navigator.clipboard
      .readText()
      .then((text) => {
        try {
          const parsedData = JSON.parse(text) as TLevelObject[];
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            addAndSelect(parsedData);
          }
        } catch (error) {
          console.error("Failed to parse clipboard data:", error);
        }
      })
      .catch((err) => {
        console.error("Failed to read from clipboard:", err);
      });
  }, [addAndSelect]);

  const handleDuplicate = useCallback(() => {
    const selectedInstances = getSelectedInstances();
    if (selectedInstances.length > 0) {
      const transformed = selectedInstances.map((obj) => ({
        ...obj,
        position: obj.position.map((coord) => coord + 1) as [number, number],
      }));
      addAndSelect(transformed);
    }
  }, [getSelectedInstances, addAndSelect]);

  const handleDelete = useCallback(() => {
    deleteSelected();
  }, [deleteSelected]);

  return {
    handleCopy,
    handlePaste,
    handleDuplicate,
    handleDelete,
  };
};
